import { Link } from "react-router-dom";
import { MapPin, Phone, Music2 } from "lucide-react";

// Exact directory order per owner directive — do not reorder or rename.
const DIRECTORY_ITEMS = [
  { label: "Music Royalties Rights Agency's", to: "/agencies" },
  { label: "Rights Library", to: "/rights-library" },
  { label: "Whois", href: "https://www.whois.com/" },
  { label: "ICann", href: "https://www.icann.org/" },
  { label: "Theatre Rights", to: "/rights-topic/theatrical-performance-rights" },
  { label: "Publishers", to: "/category/music_publisher" },
  { label: "Domains", to: "/category/domain_infra" },
  { label: "Licensing", to: "/licensing" },
  { label: "Translation Hub", to: "/translate" },
  { label: "Orchestration/Orchestrals", to: "/orchestration" },
  { label: "Delta Blues", to: "/rights-library" },
  { label: "Jazz", to: "/rights-library" },
  { label: "Classical", to: "/rights-library" },
  { label: "Gospel", to: "/rights-library" },
  { label: "Indian Music", to: "/rights-library" },
  { label: "Eastern Music", to: "/rights-library" },
  { label: "Marching Bands", to: "/rights-library" },
  { label: "Engineers", to: "/category/masters_analytics_legal" },
  { label: "U.S. Copyright Royalty Board", href: "https://www.crb.gov/" },
];

export default function SiteFooter() {
  return (
    <footer
      className="border-t border-white/[0.06] bg-[#080E1F] text-slate-400"
      data-testid="site-footer"
    >
      <div className="max-w-7xl mx-auto px-6 py-12 grid md:grid-cols-12 gap-10">
        <div className="md:col-span-3">
          <Link to="/" className="flex items-center gap-2.5 group" data-testid="footer-logo">
            <div className="w-9 h-9 rounded-md bg-gradient-to-br from-[#7DA3D9] to-[#3F5B8A] flex items-center justify-center shadow-[0_0_15px_rgba(125,163,217,0.25)]">
              <Music2 className="w-5 h-5 text-[#0A1128]" strokeWidth={2.5} />
            </div>
            <div>
              <div className="font-display text-xl text-white tracking-tight">OSRR</div>
              <div className="font-display text-[10px] text-[#7DA3D9] block leading-none mt-0.5 tracking-[0.25em] font-semibold">
                OVER SEAS RIGHTS &amp; ROYALTIES, INC.
              </div>
            </div>
          </Link>
          <p className="text-sm leading-relaxed mt-5 max-w-sm">
            The professional registry for international musical rights. Over 200 agencies across 160
            countries. Built for composers, songwriters, recording artists, orchestras, publishers,
            producers, and arrangers.
          </p>
        </div>

        <div className="md:col-span-3">
          <div className="overline mb-4">Office &amp; contact</div>
          <address className="not-italic text-sm leading-relaxed text-slate-300 space-y-3" data-testid="footer-contact">
            <div className="flex items-start gap-2.5">
              <MapPin className="w-4 h-4 text-[#7DA3D9] mt-0.5 flex-shrink-0" />
              <div>
                Over Seas Rights &amp; Royalties<br />
                1624 S. Hope St. STE 301<br />
                Los Angeles, CA 90015<br />
                United States
              </div>
            </div>
            <a
              href="tel:+13109057115"
              className="flex items-center gap-2.5 hover:text-[#B8D0EE] transition-colors"
              data-testid="footer-phone"
            >
              <Phone className="w-4 h-4 text-[#7DA3D9] flex-shrink-0" />
              <span className="notranslate">(310) 905-7115</span>
            </a>
            <a
              href="mailto:registry@osrrglobal.com"
              className="flex items-center gap-2.5 hover:text-[#B8D0EE] transition-colors"
              data-testid="footer-email"
            >
              <svg className="w-4 h-4 text-[#7DA3D9] flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"/></svg>
              <span className="notranslate">registry@osrrglobal.com</span>
            </a>
          </address>
        </div>

        {/* ============ Directory & Sections (owner-locked order) ============ */}
        <div className="md:col-span-4">
          <div className="text-white font-display font-bold text-base mb-4 tracking-tight" data-testid="footer-directory-heading">
            Directory &amp; Sections
          </div>
          <ol className="space-y-2 text-sm list-none" data-testid="footer-directory">
            {DIRECTORY_ITEMS.map((item, i) => {
              const idx = (
                <span className="font-display text-[#D4AF37] w-6 flex-shrink-0 text-right pr-1">
                  {i + 1}.
                </span>
              );
              const cls = "hover:text-white transition-colors";
              const testId = `footer-dir-${i + 1}`;
              return (
                <li key={item.label} className="flex items-baseline gap-1">
                  {idx}
                  {item.to ? (
                    <Link to={item.to} className={cls} data-testid={testId}>{item.label}</Link>
                  ) : (
                    <a href={item.href} target="_blank" rel="noopener noreferrer" className={cls} data-testid={testId}>
                      {item.label}
                    </a>
                  )}
                </li>
              );
            })}
          </ol>
        </div>

        {/* ============ Legal (owner-locked: 3 items only) ============ */}
        <div className="md:col-span-2">
          <div className="text-white font-display font-bold text-base mb-4 tracking-tight" data-testid="footer-legal-heading">
            Legal
          </div>
          <ul className="space-y-2 text-sm" data-testid="footer-legal">
            <li><Link to="/legal/terms" className="hover:text-white transition-colors" data-testid="footer-terms">Terms and Conditions</Link></li>
            <li><Link to="/legal/privacy" className="hover:text-white transition-colors" data-testid="footer-privacy">Privacy Policy</Link></li>
            <li><Link to="/legal/disclaimer" className="hover:text-white transition-colors" data-testid="footer-disclaimer">Disclaimer</Link></li>
          </ul>
        </div>
      </div>

      <div className="border-t border-white/[0.04]">
        <div className="max-w-7xl mx-auto px-6 py-5 flex flex-wrap items-center justify-between text-xs text-slate-500 gap-3">
          <span data-testid="footer-copyright">
            © {new Date().getFullYear()} Over Seas Rights &amp; Royalties · OSRR · International Music Rolodex
          </span>
          <Link to="/sitemap" className="hover:text-white transition-colors" data-testid="footer-sitemap">Site Map</Link>
        </div>
      </div>
    </footer>
  );
}
